import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  Paper,
  useTheme,
} from "@mui/material";
import React, { memo } from "react";

/**
 * CellDetailModal Component - Shows detailed view of a cell with before/after changes
 * Displays original content, new content, and change type information
 * Extracted from main PdfTableViewer to improve maintainability
 */
const CellDetailModal = ({
  // Dialog state
  open,
  onClose,

  // Cell detail data
  selectedCellDetail,
}) => {
  const theme = useTheme();
  const borderColor = theme.brand.gray.gainsboro;
  return (
    <Dialog open={open} onClose={onClose} maxWidth={"md"} fullWidth>
      <DialogTitle>{"Cell Details"}</DialogTitle>
      <DialogContent>
        {selectedCellDetail && (
          <>
            <Typography variant={"h6"} sx={{ mb: 2 }}>
              {`${selectedCellDetail.headerName} - Row ${
                selectedCellDetail.rowIndex + 1
              }`}
            </Typography>

            {selectedCellDetail.hasChange && selectedCellDetail.changeInfo ? (
              <>
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant={"subtitle1"}
                    sx={{ mb: 1, fontWeight: "bold", color: "#d32f2f" }}
                  >
                    {"Before (Original):"}
                  </Typography>
                  <Paper
                    sx={{
                      p: 2,
                      backgroundColor: "#f8d7da",
                      border: "1px solid #dc3545",
                    }}
                  >
                    <Typography
                      variant={"body1"}
                      style={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {selectedCellDetail.originalContent || "(empty)"}
                    </Typography>
                  </Paper>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant={"subtitle1"}
                    sx={{ mb: 1, fontWeight: "bold", color: "#2e7d32" }}
                  >
                    {"After (New):"}
                  </Typography>
                  <Paper
                    sx={{
                      p: 2,
                      backgroundColor: "#d4edda",
                      border: "1px solid #28a745",
                    }}
                  >
                    <Typography
                      variant={"body1"}
                      style={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {selectedCellDetail.newContent || "(empty)"}
                    </Typography>
                  </Paper>
                </Box>

                <Box
                  sx={{
                    p: 2,
                    backgroundColor: "#e3f2fd",
                    borderRadius: 1,
                  }}
                >
                  <Typography variant={"body2"} sx={{ fontWeight: "bold" }}>
                    {"Change Type: "}
                    <span
                      style={{
                        color:
                          selectedCellDetail.changeInfo.type === "added"
                            ? "#2e7d32"
                            : selectedCellDetail.changeInfo.type === "removed"
                            ? "#d32f2f"
                            : "#1976d2",
                      }}
                    >
                      {selectedCellDetail.changeInfo.type.toUpperCase()}
                    </span>
                  </Typography>
                </Box>
              </>
            ) : (
              <Box>
                <Typography
                  variant={"subtitle1"}
                  sx={{ mb: 1, fontWeight: "bold" }}
                >
                  {"Content:"}
                </Typography>
                <Paper
                  sx={{
                    p: 2,
                    backgroundColor: "#f5f5f5",
                    border: `1px solid ${borderColor}`,
                  }}
                >
                  <Typography
                    variant={"body1"}
                    style={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {selectedCellDetail.cellContent || "(empty)"}
                  </Typography>
                </Paper>
                <Typography
                  variant={"body2"}
                  color={"text.secondary"}
                  sx={{ mt: 1 }}
                >
                  {"This cell has no changes."}
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{"Close"}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(CellDetailModal);
